/**
 * UI Spec Cache Utility
 *
 * Caches UI spec data in localStorage to reduce API calls.
 * All cached data is stored under a single localStorage key: 'zenmako_ui_spec_cache'
 * Cache TTL: 1 hour
 */

const CACHE_KEY = 'zenmako_ui_spec_cache';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface CacheStore {
  [key: string]: CacheEntry<any>;
}

/**
 * Get the entire cache store from localStorage
 */
function getCacheStore(): CacheStore {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) {
      return {};
    }
    return JSON.parse(cached) as CacheStore;
  } catch (error) {
    console.warn('Failed to read UI spec cache from localStorage:', error);
    return {};
  }
}

/**
 * Save the entire cache store to localStorage
 */
function setCacheStore(store: CacheStore): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(store));
  } catch (error) {
    console.warn('Failed to write UI spec cache to localStorage:', error);
  }
}

/**
 * Generate cache key for a specific UI spec
 */
function generateCacheKey(teamId: string, connectorId: string, toolName: string): string {
  return `${teamId}:${connectorId}:${toolName}`;
}

/**
 * Check if a cache entry is still valid
 */
function isEntryValid<T>(entry: CacheEntry<T> | undefined): entry is CacheEntry<T> {
  if (!entry) {
    return false;
  }
  const now = Date.now();
  return (now - entry.timestamp) < CACHE_TTL_MS;
}

/**
 * Get cached UI spec data
 * @returns Cached data if valid, undefined otherwise
 */
export function getCachedUISpec<T>(
  teamId: string,
  connectorId: string,
  toolName: string
): T | undefined {
  const store = getCacheStore();
  const key = generateCacheKey(teamId, connectorId, toolName);
  const entry = store[key];

  if (isEntryValid(entry)) {
    console.log('✅ [UI Spec Cache] HIT:', key);
    return entry.data as T;
  }

  console.log('❌ [UI Spec Cache] MISS:', key);
  return undefined;
}

/**
 * Set UI spec data in cache
 */
export function setCachedUISpec<T>(
  teamId: string,
  connectorId: string,
  toolName: string,
  data: T
): void {
  const store = getCacheStore();
  const key = generateCacheKey(teamId, connectorId, toolName);

  store[key] = {
    data,
    timestamp: Date.now()
  };

  setCacheStore(store);
  console.log('💾 [UI Spec Cache] SET:', key);
}

/**
 * Clear all UI spec cache data
 * This is useful for debugging or when you want to force fresh data
 */
export function clearUISpecCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
    console.log('🗑️ [UI Spec Cache] CLEARED');
  } catch (error) {
    console.warn('Failed to clear UI spec cache:', error);
  }
}

/**
 * Clear expired entries from cache (garbage collection)
 * This helps keep localStorage clean
 */
export function cleanExpiredUISpecCache(): void {
  const store = getCacheStore();
  const now = Date.now();
  let removedCount = 0;

  const newStore: CacheStore = {};
  for (const [key, entry] of Object.entries(store)) {
    if ((now - entry.timestamp) < CACHE_TTL_MS) {
      newStore[key] = entry;
    } else {
      removedCount++;
    }
  }

  if (removedCount > 0) {
    setCacheStore(newStore);
    console.log(`🧹 [UI Spec Cache] Cleaned ${removedCount} expired entries`);
  }
}

/**
 * Get cache statistics (useful for debugging)
 */
export function getUISpecCacheStats(): {
  totalEntries: number;
  validEntries: number;
  expiredEntries: number;
} {
  const store = getCacheStore();
  const now = Date.now();
  let validCount = 0;
  let expiredCount = 0;

  for (const entry of Object.values(store)) {
    if ((now - entry.timestamp) < CACHE_TTL_MS) {
      validCount++;
    } else {
      expiredCount++;
    }
  }

  return {
    totalEntries: Object.keys(store).length,
    validEntries: validCount,
    expiredEntries: expiredCount
  };
}

// Expose cache management functions to window for easy debugging
if (typeof window !== 'undefined') {
  (window as any).zenmakoUISpecCache = {
    clear: clearUISpecCache,
    clean: cleanExpiredUISpecCache,
    stats: getUISpecCacheStats
  };
}
